import Axios, { AxiosResponse } from "axios";
import { from, Observable } from "rxjs";
import { map } from "rxjs/operators";

import { Customer } from "../customer/customer";
import { Events } from "../shared/analytics/analytics.constants";
import { quoteAmplitudeHelper } from "../shared/analytics/analytics.utils";
import { getConfig } from "../shared/config.utils";
import { DASH_API, GATEWAY_API, QUOTE_BFF } from "../shared/url.constants";
import { CreateEstimateRequest } from "./create-estimate.request";
import { CreateEstimateResponse } from "./create-estimate.response";
import {
  CreateEstimateResult,
  Estimate,
  EstimateAcceptResponse,
  Term,
} from "./estimate";
import { errorStatusResponses, RateEstimateStatus } from "./estimate-status";
import { GetRateEstimateResponse } from "./get-estimate.response";

export const getRateEstimate = async (
  wisrApplicationId: string,
  bid?: string
) => {
  const output = new Promise<AxiosResponse<GetRateEstimateResponse>>(
    (resolve, reject) => {
      let count = 300;
      const queryParam = bid ? `?partnerCompanyId=${bid}` : "";

      async function getRateEstimateResponse() {
        let result: AxiosResponse<GetRateEstimateResponse>;

        try {
          result = await Axios.get<GetRateEstimateResponse>(
            `${DASH_API}/api/v1/RateEstimate/${wisrApplicationId}${queryParam}`
          );
        } catch (e) {
          reject(e);
          return;
        }

        if (errorStatusResponses.includes(result.data.status)) {
          quoteAmplitudeHelper.sendEvent(Events.SERVER_ERROR, {
            Type: "Rate Estimate Form",
            Code: result.data.status,
            Page: window.location.href,
          });

          reject(
            new Error(
              `An error has occurred. Please try again later and if the problem persists, please contact Wisr for support Code:${result.data.status}`
            )
          );
          return;
        }

        if (
          result &&
          result.data &&
          result.data.status !== RateEstimateStatus.PENDING
        ) {
          resolve(result);
        } else {
          if (count <= 0) {
            reject(
              new Error(
                "Exceeded maximum amount of calls to get quote endpoint"
              )
            );
            return;
          } else {
            count--;
            setTimeout(getRateEstimateResponse, 1000);
          }
        }
      }
      getRateEstimateResponse();
    }
  );
  return output;
};

export function getEstimateById(
  estimateId: string,
  bid?: string
): Observable<{ estimate: Estimate; customer: Customer }> {
  const output = new Promise<AxiosResponse<GetRateEstimateResponse>>(
    (resolve, reject) => {
      async function callGetQuote() {
        let rateEstimate: AxiosResponse<GetRateEstimateResponse>;

        try {
          rateEstimate = await getRateEstimate(estimateId, bid);
        } catch (e) {
          reject(e);
          return;
        }

        const RESOLVE_DELAY = 3000;
        setTimeout(() => {
          resolve(rateEstimate);
        }, RESOLVE_DELAY);
      }
      callGetQuote();
    }
  );

  return from(output).pipe(
    map((res) => {
      return {
        estimate: {
          loanId: res.data.loanId,
          quoteId: res.data.quoteId,
          status: res.data.status,
          loanAmount: res.data.rateEstimate?.requestedLoanAmount ?? 0,
          loanPurpose: res.data.rateEstimate?.loanPurpose ?? "",
          establishmentFee: res.data.rateEstimate?.fees?.establishmentFee ?? 0,
          ppsrFee: res.data.rateEstimate?.fees?.ppsrFee ?? 0,
          privateSaleFee: res.data.rateEstimate?.fees?.privateSaleFee ?? 0,
          monthlyAccountFee:
            res.data.rateEstimate?.fees?.monthlyAccountFee ?? 0,
          totalEstablishmentFee:
            res.data.rateEstimate?.fees?.totalEstablishmentFee ?? 0,
          isSecuredLoan: res.data.rateEstimate?.rateType === "Secured",
          isDrivaEligible: res.data.isDrivaEligible,
          partnerCompanyId: res.data.partnerCompanyId ?? "",
          offers: res.data.rateEstimate?.indicativeQuotes
            ? res.data.rateEstimate.indicativeQuotes.map((offer) => {
                return {
                  term: (offer.term * 12) as Term,
                  rate: offer.rate,
                  repayment: offer.monthlyRepayment,
                  loanLimit: offer.eligibleLoanAmount,
                };
              })
            : [],
        },
        customer: {
          id: res.data.wisrCustomerId ?? "",
          firstname: res.data.firstName ?? "",
        },
      };
    })
  );
}

export function createEstimate(
  createEstimateRequest: CreateEstimateRequest,
  wcToken?: string
): Observable<CreateEstimateResult> {
  return from(
    Axios.post<CreateEstimateResponse>(
      `${DASH_API}/api/v1/RateEstimate`,
      createEstimateRequest,
      {
        headers: {
          "content-type": "application/json",
          ...(wcToken && { Authorization: `Bearer ${wcToken}` }),
        },
      }
    ).then((res) => {
      return {
        wisrApplicationId: res.data.wisrApplicationId,
        partnerCompanyId: createEstimateRequest.partnerCompanyId,
      };
    })
  );
}

export function estimateAccepted(
  quoteId: string,
  term: Term
): Observable<EstimateAcceptResponse> {
  return from(
    Axios.post<EstimateAcceptResponse>(
      `${DASH_API}/api/v1/RateEstimate/accept`,
      { quoteId: quoteId, term: term },
      {
        headers: {
          "content-type": "application/json",
          "x-auth-key": getConfig().ESTIMATE_ACCEPT_KEY,
        },
      }
    ).then((res) => {
      return {
        email: res.data.email,
        wisrApplicationId: res.data.wisrApplicationId,
      };
    })
  );
}

export function setLoanterm(
  loanId: number,
  quoteId: string,
  loanterm: number
): Observable<void> {
  return from(
    Axios.post<void>(`${GATEWAY_API}/api/gateway/loanterm`, {
      LoanId: loanId,
      LoanQuoteId: quoteId,
      LoanTerm: loanterm,
    }).then(() => undefined)
  );
}

export function updateLoanAmount(estimate: Estimate): Observable<void> {
  return from(
    Axios.put<void>(
      `${QUOTE_BFF}/quote/${estimate.quoteId}/loan-amount`,
      estimate.loanAmount,
      { headers: { "content-type": "Application/JSON" } }
    ).then(() => undefined)
  );
}

export function updateQuoteJourney(quoteId: string): Observable<void> {
  return from(
    Axios.put<void>(`${QUOTE_BFF}/quote/${quoteId}/journey`, {
      headers: { "content-type": "Application/JSON" },
    }).then(() => undefined)
  );
}
